import { useEffect, useMemo, useState } from "react"
import services from "../../../services/services";
import _ from "underscore";
import { decryptData, encryptData } from "../../../utils/encrypt";
import { dateConvert, formatDateObject } from "../../../utils/Helper";
import { DateObject } from "react-multi-date-picker";

export const useStreamSet = ({ projectChanged }) => {

    const [streamsetData, setStreamsetData] = useState({ x_axis: [], y_axis: [], api_data: [] });
    const [showPopup, setShowPopup] = useState(false);
    const [popupData, setPopupData] = useState([]);
    const [hoveredRange, setHoveredRange] = useState([]);
    const [loader, setLoader] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [filterOpts, setFilterOpts] = useState({ pipeline: [] });
    const [filterValues, setFilterValues] = useState({ pipeline: [], date: {} });
    const [filterCount, setFilterCount] = useState(0);
    const [expand, setExpand] = useState(false);
    const [time, setTime] = useState({ from: null, to: null });
    const [intervalFlag, setIntervalFlag] = useState(false);
    const [defaultClicked, setDefaultClicked] = useState(true);

    useEffect(() => {
        handleDateRange(null, true);
    }, [])

    useEffect(() => {
        getData()
    }, [projectChanged])

    useEffect(() => {
        const interval = setInterval(() => {
            if (defaultClicked) {
                handleDateRange(null, true);
            } else {
                getData();
            }
        }, 120000);

        return () => clearInterval(interval);
    }, [intervalFlag]);

    const handleDateRange = (e, defaultFlag) => {
        if (e === null) {
            const now = new DateObject();
            const oneMinuteAgo = new DateObject().subtract(1, "hour");
            const range = [new DateObject(oneMinuteAgo), new DateObject(now)];
            setHoveredRange(range);
            setTime({ from: oneMinuteAgo?.toDate(), to: now?.toDate() });
            defaultFlag && handleApplyFilters(range) && setDefaultClicked(true);
        } else {
            setHoveredRange(e);
            setDefaultClicked(false);
        }
    }

    const handleApplyFilters = (dateRange) => {
        let dates;
        if (dateRange) {
            dates = {
                from_date: dateConvert(dateRange?.[0]?.toDate(), "iso-string")?.split(".")?.[0],
                to_date: dateConvert(dateRange?.[1]?.toDate(), "iso-string")?.split(".")?.[0]
            }
        } else {
            dates = {
                from_date: formatDateObject(hoveredRange?.[0], time?.from),
                to_date: formatDateObject(hoveredRange?.[1], time?.to)
            }
        }
        const streamFilters = {
            dates: dates,
            pipeline: filterValues?.pipeline,
        }
        sessionStorage.setItem("streamfilters", encryptData(JSON.stringify(streamFilters)));

        // set count of filters applied
        let count = 0;
        if ((hoveredRange === undefined || _.isEmpty(hoveredRange)) && (dateRange === undefined || _.isEmpty(dateRange))) {
            count = 0;
        } else {
            count++;
        }
        Object.keys(filterValues)?.forEach((item) => {
            if (filterValues[item].length > 0) {
                count++;
            }
        })
        setFilterCount(count);
        setShowFilter(false);
        sessionStorage.getItem("inspectra_error_view") && getData();
        setIntervalFlag(!intervalFlag);
    }

    const handleDefaultFilters = () => {
        handleDateRange(null, false);
        setDefaultClicked(true);
        setFilterValues((prev) => ({ ...prev, pipeline: [] }));
    }

    const getData = async () => {
        try {
            const selectedProject = sessionStorage.getItem("inspectra_project") ? JSON.parse(decryptData(sessionStorage.getItem("inspectra_project"))) : {};
            if (!_.isEmpty(selectedProject)) {
                setLoader(true);
                const filters = JSON.parse(decryptData(sessionStorage.getItem("streamfilters")) || "{}");
                setFilterValues((prev) => ({ ...prev, date: filters?.dates }));
                const payload = {
                    type: 'streamsets',
                    fromDate: filters?.dates?.from_date,
                    toDate: filters?.dates?.to_date,
                    pipelinename: _.isEmpty(filters?.pipeline) ? undefined : filters?.pipeline?.toString(),
                    project_name: selectedProject?.project_key
                }
                const response = await services.callInspectra("GET", '/elsinspectra/top', payload);
                if (response) {
                    const data = response?.data;
                    const streamsets = _.uniq(_.pluck(data, "pipelinename"));
                    let arr = [];
                    streamsets?.forEach((item) => {
                        arr.push(parseInt(_.filter(data, { pipelinename: item })?.length));
                    })
                    if (_.isEmpty(filterValues?.pipeline)) {
                        setFilterOpts({ pipeline: streamsets });
                    }
                    setStreamsetData({ x_axis: streamsets, y_axis: arr, api_data: data });
                    setLoader(false);
                }
            }
        } catch (error) {
            console.error("Error ", error);
            setLoader(false);
        }
    }

    const options = {
        credits: { enabled: false },
        chart: {
            type: 'column',
            events: {
                render: function () {
                    const chart = this;
                    chart.xAxis[0].ticks &&
                        Object.values(chart.xAxis[0].ticks).forEach(tick => {
                            if (tick.label && !tick.label.clickAttached) {
                                tick.label.element.style.cursor = 'pointer';
                                tick.label.element.onclick = () => {
                                    handleShowDetailsPopup(tick.label.textStr);
                                };
                                tick.label.clickAttached = true;
                            }
                        });
                }
            }
        },
        title: {
            text: ''
        },
        xAxis: {
            categories: streamsetData?.x_axis,
            gridLineDashStyle: 'solid',
            gridLineWidth: 1
        },
        yAxis: {
            title: {
                text: ''
            },
            tickInterval: 1,
        },
        plotOptions: {
            series: {
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            handleShowDetailsPopup(this.category);
                        }
                    }
                },
                enableMouseTracking: true,
                showInLegend: false,
            },
        },
        series: [
            {
                name: "Failing Count",
                data: streamsetData?.y_axis,
                color: "#9DB8F8"
            },
        ],
    }

    const handleShowDetailsPopup = (pipeline) => {
        const data = _.filter(streamsetData?.api_data, { pipelinename: pipeline });
        setPopupData(data);
        setShowPopup(true);
    }

    return useMemo(() => ({
        options, showPopup, setShowPopup, popupData, hoveredRange, setHoveredRange, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, time, setTime, handleDateRange, handleApplyFilters, handleDefaultFilters
    }), [options, showPopup, setShowPopup, popupData, hoveredRange, setHoveredRange, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, time, setTime, handleDateRange, handleApplyFilters, handleDefaultFilters])
}